import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { AlertTriangle, CreditCard, ExternalLink, Rocket } from 'lucide-react';
import { useState } from 'react';
/**
 * Persisted failures must remain readable after the launch dialog is closed.
 *
 * `onRetry` is set when the failed ad never reached Meta, so the owner can
 * launch it again from here once the cause is fixed, instead of being told to
 * "try again" with nothing to press. It opens the table's one launch dialog.
 */
export function AdSyncError({
  message,
  onRetry,
  label = 'Action needed',
  metaAdId,
}: {
  message: string;
  onRetry?: () => void;
  label?: string;
  metaAdId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const instagramChoice =
    /instagram/i.test(message) &&
    /Meta Products for free|subscribe.*without ads/i.test(message);
  const missingPayment = /\b(no|missing) payment method\b/i.test(message);
  const payment = /payment|billing|funding|credit card/i.test(message);
  const connection = /token|permission|reconnect|access.*expired/i.test(
    message
  );
  const restricted = /disabled|restricted|with.issues/i.test(message);
  const action = instagramChoice
    ? {
        label: 'Open Accounts Centre',
        href: 'https://accountscenter.facebook.com/ads/',
      }
    : payment
      ? {
          label: 'Review Meta billing',
          href: 'https://business.facebook.com/billing_hub',
        }
      : connection
        ? {
            label: 'Review Meta connection',
            href: '/dashboard/settings/integrations',
          }
        : restricted
          ? {
              label: 'Review Meta account',
              href: 'https://business.facebook.com/accountquality',
            }
          : metaAdId
            ? {
                label: 'Review ad in Meta',
                href: `https://adsmanager.facebook.com/adsmanager/manage/ads?selected_ad_ids=${encodeURIComponent(metaAdId)}`,
              }
            : null;
  const external = action?.href.startsWith('https:');
  const Icon = payment ? CreditCard : AlertTriangle;
  return (
    <div
      className="text-sm"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
    >
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-300 dark:hover:bg-amber-950/50"
          >
            <AlertTriangle aria-hidden="true" className="size-3.5 shrink-0" />
            <span className="max-w-56 truncate">{label}</span>
          </button>
        </DialogTrigger>
        <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] min-w-0 max-w-[440px] gap-0 overflow-y-auto rounded-2xl p-0 sm:max-w-[440px]">
          <DialogHeader className="min-w-0 gap-4 p-6 pb-5 text-left">
            <div className="flex size-11 items-center justify-center rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
              <Icon aria-hidden="true" className="size-5" />
            </div>
            <div className="space-y-2">
              <DialogTitle className="pr-5 text-xl leading-tight tracking-tight">
                {instagramChoice
                  ? 'Choose free of charge with ads'
                  : missingPayment
                    ? 'No payment method added'
                    : payment
                      ? 'Review your payment method'
                      : 'Ad needs attention'}
              </DialogTitle>
              <DialogDescription className="text-left leading-relaxed">
                {instagramChoice
                  ? 'The Instagram account owner must make this choice in Meta Accounts Centre to run ads.'
                  : onRetry
                    ? action
                      ? 'Resolve the issue below, then retry the launch.'
                      : 'Fix what is described below, then retry the launch.'
                    : action
                      ? 'Resolve the issue below, then return to your ad to try again.'
                      : 'Review the details below to see what needs to change.'}
              </DialogDescription>
            </div>
          </DialogHeader>
          <div className="min-w-0 px-6 pb-6">
            {instagramChoice && (
              <div className="mb-5 space-y-3 text-sm leading-relaxed">
                <ol className="list-decimal space-y-2 pl-5">
                  <li>
                    Open Accounts Centre for the Instagram account named in
                    Meta’s message below.
                  </li>
                  <li>
                    Under Ad preferences, select{' '}
                    <strong>Ads on Meta Company Products</strong>.
                  </li>
                  <li>
                    Choose <strong>Free of charge with ads</strong> and complete
                    Meta’s prompts.
                  </li>
                </ol>
                <p>
                  Return to Borrah and check that Meta has cleared the
                  restriction before resuming the ad.
                </p>
              </div>
            )}
            <div className="space-y-2 rounded-xl border bg-muted/40 p-4">
              <p className="text-xs font-medium text-muted-foreground">
                What happened
              </p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]">
                {missingPayment
                  ? 'This ad account has no payment method. Add one in Meta to launch your ad.'
                  : message}
              </p>
            </div>
          </div>
          {(action || onRetry) && (
            <div className="min-w-0 space-y-3 border-t bg-muted/20 px-6 py-5">
              {onRetry && (
                <Button
                  type="button"
                  variant={action ? 'outline' : 'default'}
                  className="h-11 w-full rounded-lg"
                  onClick={() => {
                    setOpen(false);
                    onRetry();
                  }}
                >
                  <Rocket aria-hidden="true" className="size-4" />
                  Retry launch
                </Button>
              )}
              {action && (
                <Button asChild className="h-11 w-full rounded-lg">
                  <a
                    href={action.href}
                    {...(external
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {action.label}
                    {external && (
                      <ExternalLink aria-hidden="true" className="size-4" />
                    )}
                  </a>
                </Button>
              )}
              {action && external && (
                <p className="text-center text-xs leading-relaxed text-muted-foreground">
                  {instagramChoice
                    ? 'Opens Meta in a new tab. Use the Accounts Centre linked to the Instagram account in the error.'
                    : payment
                      ? 'Opens Meta in a new tab. Select the ad account for this campaign.'
                      : 'Opens Meta in a new tab.'}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
